import React, { useCallback, useEffect, useState } from "react";
import { SSH_User } from "@danishagro/sharedShop/src/interfaces/user.interface";
import { useRolesApi } from "@danishagro/sharedShop/src/hooks/api/useRolesApi.hook";
import { useUserRolesApi } from "@danishagro/sharedShop/src/hooks/api/useUserRolesApi.hook";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { SSH_Role } from "@danishagro/sharedShop/src/interfaces/role.interface";
import { ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_ConfirmModal } from "@danishagro/shared/src/components/molecules/ConfirmModal/ConfirmModal.component";
import { DA_ButtonColor } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_LinkMenu } from "@danishagro/shared/src/components/molecules/LinkMenu/LinkMenu.component";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Dropdown } from "@danishagro/shared/src/components/molecules/Dropdown/Dropdown.component";
import { DA_Table } from "@danishagro/shared/src/components/atoms/Table/Table.component";
import { getTableMatrix } from "@danishagro/shared/src/helpers/getTableMatrix.helper";
import Skeleton from "react-loading-skeleton";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { DA_Tooltip } from "@danishagro/shared/src/components/atoms/Tooltip/Tooltip.component";
import { SSH_Scope } from "@danishagro/sharedShop/src/interfaces/scope.interface";
import { SSH_ScopeGroup } from "@danishagro/sharedShop/src/interfaces/scopeGroup.interface";
import { DA_Modal } from "@danishagro/shared/src/components/molecules/Modal/Modal.component";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { _InviteUserModal } from "../../modals/InviteUserModal/InviteUserModal.component";
import { _EditUserModal } from "../../modals/EditUserModal/EditUserModal.component";
import { useAccessControl, Data } from "../../hooks/useAccessControl.hook";
import S from "./ListOfEmployeesDesktop.module.scss";

export interface Props {
    invite?: boolean;
}

export const _ListOfEmployeesDesktop = (_props: Props) => {
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [roles, setRoles] = useState<SSH_Role[]>([]);
    const [_scopes, setScopes] = useState<SSH_Scope[]>([]);
    const [_scopeGroups, setScopeGroups] = useState<SSH_ScopeGroup[]>([]);
    const [users, setUsers] = useState<SSH_User[]>([]);
    const [allowInvite, setAllowInvite] = useState<boolean>(false);
    const [batchSize] = useState(5);
    const [sortAscending, setSortAscending] = useState(false);
    const [sortByColumnIndex, setSortByColumnIndex] = useState(0);
    const { getRoles, getScopes, getScopeGroups } = useRolesApi();
    const { getUsersRaw, deleteUser, getDeletedUsers } = useUserRolesApi();
    const { cvrNumber, customerNumber } = useAppData();
    const { showModal, closeModal } = useModal();
    const { getDictionaryString, getDictionaryItem } = useTranslations();
    const [deletedUsers, setDeletedUsers] = useState<SSH_User[]>([]);
    const [showDeletedUsers, setShowDeletedUsers] = useState(false);

    // Check user if Owner, isAdmin or isMasterAdmin
    const data: Data = globalThis?.cvrAndCustomerNumbers;
    // Grant access
    const hasAccess = useAccessControl(data);

    // Fetch deleted users
    const fetchDeletedUsers = useCallback(() => {
        getDeletedUsers(customerNumber)
            .then((response) => {
                setDeletedUsers(Array.isArray(response) ? response : []);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [getDeletedUsers, customerNumber]);

    const fetchUsers = useCallback(() => {
        setShowSkeleton(true);
        getUsersRaw(customerNumber)
            .then((res) => {
                setUsers(Array.isArray(res.users) ? res.users : []);
                setAllowInvite(!!res.allowInvite);
            })
            .catch((error) => {
                setUsers([]);
                setAllowInvite(false);
                console.log(error);
            })
            .finally(() => setShowSkeleton(false));
    }, [getUsersRaw, customerNumber]);

    // Delete-user function
    const removeUser = useCallback(
        (id, customerNumber) => {
            deleteUser(id, customerNumber)
                .then(() => {
                    const newUsers = [...users].filter((item) => item.userId !== id);

                    setUsers(newUsers);
                    closeModal();
                })
                .catch((error) => console.log(error));
        },
        [closeModal, deleteUser, users]
    );

    // Delete modal
    const openDeleteModal = useCallback(
        (name, id) => {
            showModal(
                <DA_ConfirmModal
                    title={getDictionaryItem("deleteName", { name })}
                    description={getDictionaryItem("deleteModalText", {
                        name,
                    })}
                    approveButtonLabel={getDictionaryString("confirmDelete")}
                    approveButtonColor={DA_ButtonColor.Alert}
                    onApprove={() => removeUser(id, customerNumber)}
                    denyButtonLabel={getDictionaryString("cancel")}
                />,
                { size: ModalSize.XS }
            );
        },
        [showModal, getDictionaryItem, getDictionaryString, removeUser, customerNumber]
    );

    // Edit modal
    const openEditUserModal = useCallback(
        (userId, cvr, name, role) => {
            showModal(
                <DA_Modal title={getDictionaryItem("editName", { name })}>
                    <_EditUserModal id={userId} role={role} onSubmit={fetchUsers} />
                </DA_Modal>
            );
        },
        [showModal, getDictionaryItem, fetchUsers]
    );

    // Table header
    const getBaseTableColumns = () => [
        {
            title: getDictionaryString("name", { uppercaseFirst: true }),
            width: 200,
        },
        {
            title: getDictionaryString("type", { uppercaseFirst: true }),
            width: 100,
        },
        {
            title: getDictionaryString("account", { uppercaseFirst: true }),
            width: 400,
        },
        // TODO When deleted users are shown again, incomment this
        // {
        //     title: getDictionaryString("invitedBY", { uppercaseFirst: true }),
        //     width: 200,
        // },
    ];

    const tableColumns = [
        ...getBaseTableColumns(),
        {
            title: getDictionaryString("actions", { uppercaseFirst: true }),
            sortable: false,
            alignRight: true,
            width: 100,
        },
    ];

    // Table columns for deleted users
    const deletedUsersTableColumns = [
        ...getBaseTableColumns(),
        {
            title: "",
            width: 100,
        },
    ];

    const generateTableRow = (user, includeActions = true) => [
        {
            value:
                user.firstName || user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : getDictionaryItem("unnamed"),
        },
        {
            value: getDictionaryString(user.roleAlias, { uppercaseFirst: true }),
        },
        {
            value: user?.customerNumbers
                ?.slice(0, 2)
                ?.map(({ number, name }) => `${number} - ${name}`),
            isUrlCell: true,
            cellFullWidth: true,
        },
        // TODO When deleted users are shown again, incomment this
        // {
        //     value: user.invitedBy || "",
        // },
        includeActions
            ? {
                  value: user.roleAlias !== "owner" && (
                      <DA_LinkMenu
                          links={[
                              {
                                  title: getDictionaryString("editUser"),
                                  onClick: () =>
                                      openEditUserModal(
                                          `${user.userId}`,
                                          cvrNumber,
                                          user.firstName || user.lastName
                                              ? `${user.firstName} ${user.lastName}`
                                              : getDictionaryString("unnamed"),
                                          `${user.roleAlias}`
                                      ),
                              },
                              {
                                  title: getDictionaryString("deleteUser", {
                                      uppercaseFirst: true,
                                  }),
                                  onClick: () =>
                                      openDeleteModal(
                                          user.firstName || user.lastName
                                              ? `${user.firstName} ${user.lastName}`
                                              : getDictionaryString("unnamed"),
                                          user.userId
                                      ),
                              },
                          ]}
                      />
                  ),
                  alignRight: true,
              }
            : {
                  value: "",
              },
    ];

    // Cell value for getUsers
    const contractLines = users?.map((user) => generateTableRow(user));

    // Cell value for getDeletedUsers
    const deletedUsersContractLines = deletedUsers?.map((user) => generateTableRow(user, false));

    // Fetch both active and deleted users on component mount
    useEffect(() => {
        fetchUsers();
        fetchDeletedUsers();
        // ... other data fetching ...
    }, [fetchUsers, fetchDeletedUsers /* other dependencies */]);

    const openInviteModal = useCallback(
        (alias) => {
            showModal(<_InviteUserModal cvr={cvrNumber} onSubmit={fetchUsers} alias={alias} />, {
                size: ModalSize.XS,
            });
        },
        [cvrNumber, fetchUsers, showModal]
    );

    useEffect(() => {
        fetchUsers();
        Promise.all([getRoles(), getScopes(), getScopeGroups()]).then(
            ([receivedRoles, receivedScopes, receivedScopeGroups]) => {
                setRoles(receivedRoles);
                setScopes(receivedScopes);
                setScopeGroups(receivedScopeGroups);
            }
        );
    }, [getRoles, getScopes, getScopeGroups, fetchUsers]);

    // Toggle show more deleted users
    const toggleDeletedUsers = () => setShowDeletedUsers(!showDeletedUsers);

    // Calculate whether contractLines length is even or odd
    const isContractLinesEven = contractLines?.length % 2 === 0;
    const deletedTableClass = isContractLinesEven ? S.deletedTableEven : S.deletedTableOdd;

    return (
        <>
            <DA_Container>
                <div className={S.header}>
                    <div className={S.wrapper}>
                        <DA_Title h3>
                            {getDictionaryItem("listOfEmployeesTitle")}
                            <DA_Tooltip
                                text={getDictionaryItem("listOfEmployeesDescription")}
                                placement="top"
                            />
                        </DA_Title>
                    </div>
                    {allowInvite && (
                        <DA_Dropdown
                            title={getDictionaryString("inviteUser", {})}
                            items={roles.map((role) => ({
                                title: getDictionaryString(role.alias, {
                                    uppercaseFirst: true,
                                }),
                                onClick: () => openInviteModal(role.alias),
                            }))}
                            icon={DA_IconNames.Add}
                        />
                    )}
                </div>
                {showSkeleton || (Array.isArray(contractLines) && contractLines.length) ? (
                    <div className={S.usersTable}>
                        <DA_Table
                            columns={tableColumns}
                            data={
                                showSkeleton
                                    ? getTableMatrix({
                                          // TODO The number of columns. When deleted users are shown again, set columns to 5 again
                                          columns: 4,
                                          rows: batchSize,
                                          cellContent: ({ column }) => ({
                                              value: (
                                                  <Skeleton
                                                      style={{
                                                          verticalAlign: "middle",
                                                      }}
                                                      width={column === 4 ? "50px" : "80%"} // Adjust for the last column
                                                      height={column === 5 ? 35 : undefined}
                                                  />
                                              ),
                                              alignRight: column === 4,
                                          }),
                                      })
                                    : contractLines
                            }
                            sortAscending={sortAscending}
                            sortByColumnIndex={sortByColumnIndex}
                            onSortChange={(columnIndex, isAscending) => {
                                setSortByColumnIndex(columnIndex);
                                setSortAscending(isAscending);
                            }}
                        />
                    </div>
                ) : (
                    <DA_EmptyState
                        type={DA_EmptyStateTypes.NoContent}
                        title={getDictionaryItem("noEmployeesTitle")}
                        text={getDictionaryItem("noEmployeesText")}
                    />
                )}

                {/* Deleted users */}
                {!showSkeleton && hasAccess ? (
                    <div className={S.expandContainer}>
                        <DA_ButtonLink
                            title={
                                showDeletedUsers
                                    ? getDictionaryString("HideDeletedUsers")
                                    : getDictionaryString("ShowDeletedUsers")
                            }
                            onClick={toggleDeletedUsers}
                            icon={
                                showDeletedUsers
                                    ? DA_IconNames.ChevronSmallUp
                                    : DA_IconNames.ChevronSmallDown
                            }
                            className={S.expandBtn}
                        />
                    </div>
                ) : null}

                {showDeletedUsers &&
                    (deletedUsers.length > 0 ? (
                        <div className={cn(S.deletedTable, deletedTableClass)}>
                            <DA_Table
                                columns={deletedUsersTableColumns}
                                data={deletedUsersContractLines}
                            />
                        </div>
                    ) : (
                        <div className={S.emptyState}>
                            <DA_EmptyState
                                type={DA_EmptyStateTypes.NoResults}
                                title={getDictionaryItem("noDeletedEmployeesTitle")}
                                text={getDictionaryItem("noDeletedEmployeesText")}
                            />
                        </div>
                    ))}
            </DA_Container>

            {/* <DA_Container>
                <DA_TableOfRoles
                    title={getDictionaryItem("rolesAndScopesTitle")}
                    titleModal={{
                        title: getDictionaryItem("rolesAndScopesModalTitle"),
                        content: getTextItem("rolesAndScopesModalText"),
                    }}
                    roles={roles}
                    scopes={scopes}
                    scopeGroups={scopeGroups}
                />
            </DA_Container> */}
        </>
    );
};
